'use client'

import React, { useState, useEffect, useRef } from 'react'
import { Container, Grid } from '@quark/layout'
import { Text } from '@quark/typography'
import { Reveal } from '@quark/animation'
import BusinessModals from '@components/BusinessModal/businessModals'
import BenefitsCard from '../Card'
import styles from '../../benefitsCarousal.module.scss'

const ANCHOR_RATIO = 0.3

interface BenefitTab {
  id?: string | number
  title?: string
}

interface DesktopViewProps {
  title?: string
  benefitTabs?: BenefitTab[]
  benefitCards?: any[]
}

const DesktopView = ({ title, benefitTabs, benefitCards }: DesktopViewProps) => {
  const [activeTab, setActiveTab] = useState<number>(0)
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [formSnippet, setFormSnippet] = useState<string>('')
  const cardRefs = useRef<Array<HTMLDivElement | null>>([])
  const [shouldHide, setShouldHide] = useState(false)
  const shouldHideRef = useRef(false)
  const activeTabRef = useRef(0)
  const cardBoundsRef = useRef<Array<{ start: number; end: number }>>([])
  const isProgrammaticScrollRef = useRef(false)
  const programmaticScrollTimeoutRef = useRef<number | null>(null)

  const updateCardBounds = React.useCallback(() => {
    cardBoundsRef.current = cardRefs.current
      .map(card => {
        if (!card) return null
        const rect = card.getBoundingClientRect()
        const start = rect.top + window.scrollY
        const end = start + rect.height
        return { start, end }
      })
      .filter((bound): bound is { start: number; end: number } => Boolean(bound))
  }, [benefitCards?.length])

  const getActiveIndexForAnchor = React.useCallback((anchorPosition: number) => {
    // Use live DOM positions so sticky behavior is accurately reflected
    if (!cardRefs.current.length) return null

    // Choose the last card whose top has been crossed by the anchor. This avoids
    // flicker when sticky cards overlap in height.
    let nextActiveIndex = 0

    for (let i = 0; i < cardBoundsRef.current.length; i++) {
      const { start } = cardBoundsRef.current[i]
      if (anchorPosition >= start) {
        nextActiveIndex = i
      } else {
        break
      }
    }

    return nextActiveIndex
  }, [])

  const setActiveTabFromScroll = React.useCallback(() => {
    const anchorPosition = window.scrollY + window.innerHeight * ANCHOR_RATIO
    const nextActiveIndex = getActiveIndexForAnchor(anchorPosition)

    if (nextActiveIndex === null) return

    if (nextActiveIndex !== activeTabRef.current) {
      activeTabRef.current = nextActiveIndex
      setActiveTab(nextActiveIndex)
    }
  }, [getActiveIndexForAnchor])

  const handleTabClick = index => {
    const el = cardRefs.current[index]
    if (!el) return

    const offset = 120

    const y = el.getBoundingClientRect().top + window.pageYOffset - offset

    window.scrollTo({
      top: y,
      behavior: 'smooth',
    })

    setActiveTab(index)
  }

  useEffect(() => {
    let ticking = false

    const processScroll = () => {
      if (!cardBoundsRef.current.length) return

      const anchorPosition = window.scrollY + window.innerHeight * ANCHOR_RATIO
      const lastCardEnd = cardBoundsRef.current[cardBoundsRef.current.length - 1].end

      const shouldHide = anchorPosition > lastCardEnd

      if (shouldHide !== shouldHideRef.current) {
        shouldHideRef.current = shouldHide
        setShouldHide(shouldHide)
      }

      if (benefitCards?.length && !isProgrammaticScrollRef.current) {
        setActiveTabFromScroll()
      }
    }

    const handleScroll = () => {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        processScroll()
        ticking = false
      })
    }

    const handleResize = () => {
      updateCardBounds()
      if (benefitCards?.length) {
        setActiveTabFromScroll()
      }
    }

    // Initialize on mount
    requestAnimationFrame(() => {
      updateCardBounds()
      processScroll()
    })

    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleResize)
      if (programmaticScrollTimeoutRef.current !== null) {
        clearTimeout(programmaticScrollTimeoutRef.current)
      }
    }
  }, [benefitCards?.length])

  return (
    <Container
      type="fluid"
      paddingTop={{ lg: 'spacing-72', md: 'spacing-56', sm: 'spacing-40' }}
      backgroundColor="base/overlay">
      <Container type="fluid" position="relative">
        <Reveal as="span" animationProps={{ threshold: 0.1, animationDelayMs: 100 }}>
          <Container type="max-parent" paddingBottom={{ lg: 'spacing-48', md: 'spacing-32' }}>
            <Text
              as="h1"
              fontStyle={{ lg: 'um_text-5xl/bold', md: 'um_text-3xl/bold', sm: 'um_text-2xl/bold' }}
              color="base/primary"
              textAlign={{ lg: 'center', md: 'center', sm: 'center' }}
              dangerouslySetInnerHTML={{ __html: title || '' }}
            />
          </Container>
        </Reveal>
        <div className={`${styles.stickyTabsContainer} ${shouldHide ? styles.hidden : ''}`}>
          <Reveal as="span" animationProps={{ threshold: 0.1, animationDelayMs: 200 }}>
            <Container type="root">
              <Grid
                gap={{
                  lg: 'spacing-32',
                  md: 'spacing-24',
                  sm: 'spacing-16',
                }}
                justifyContent="space-between"
                gridTemplateColumns="auto-fit">
                {benefitTabs?.map((tab, index) => (
                  <div
                    key={tab?.id}
                    className={`${styles.tab} ${activeTab === index ? styles.activeTab : ''}`}
                    onClick={() => handleTabClick(index)}>
                    <Text
                      fontStyle={{
                        lg: 'um_text-2xl/medium',
                        md: 'um_text-lg/medium',
                        sm: 'um_text-base/medium',
                      }}
                      color={activeTab === index ? 'base/primary' : 'base/secondary'}>
                      {tab?.title}
                    </Text>
                  </div>
                ))}
              </Grid>
            </Container>
          </Reveal>
        </div>
        <Container type="root">
          {benefitCards?.map((card, index) => (
            <div
              ref={el => {
                cardRefs.current[index] = el
              }}
              className={styles.benefitsCardContainer}
              key={index}>
              <div className={styles.benefitsCard}>
                <BenefitsCard data={card} setIsModalOpen={setIsModalOpen} setFormSnippet={setFormSnippet} />
              </div>
            </div>
          ))}
          <div style={{ height: '5vh' }} />
        </Container>
      </Container>
      <BusinessModals isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} content={formSnippet} />
    </Container>
  )
}

export default DesktopView
